import { logInfo } from '@core/logging'
import type {
  PhoenixPushEventMap,
  PhoenixPushMessage,
  PhoenixServerEventMap,
  PhoenixServerMessage,
  Version,
} from '@core/phoenix/core/types'

import { Channel } from './channel'
import { DEFAULT_TIMEOUT, SOCKET_STATES, WS_CLOSE_NORMAL } from './constants'
import { decode, encode } from './serializer'
import { Timer } from './timer'

interface Options {
  /** The ms interval to send a heartbeat message. */
  heartbeatIntervalMs?: number

  /**
   * The function that returns the ms socket reconnect interval.
   * Defaults to stepped backoff.
   */
  reconnectAfterMs?: (tries: number) => number

  /** The function that returns the ms rejoin interval. */
  rejoinAfterMs?: (tries: number) => number

  /** The timeout in ms to trigger push timeouts. */
  pushTimeout?: number

  /** The URL params to pass when connecting. */
  params?: Record<string, string>

  /** The serializer protocol version to send on connect. */
  vsn?: Version
}

/** Socket class */
export class Socket {
  private stateChangeCallbacks: {
    open: Array<[string, () => void]>
    close: Array<[string, (closeEvent: CloseEvent) => void]>
    error: Array<[string, (event: Event) => void]>
    message: Array<[string, (msg: PhoenixServerMessage) => void]>
  } = { open: [], close: [], error: [], message: [] }

  /** The number of established connections. */
  private establishedConnections = 0

  /** The reconnect timer. */
  private reconnectTimer: Timer

  /** The WebSocket connection. */
  private conn: WebSocket | undefined

  /** The current ref count. */
  private ref = 0

  /**
   * The buffer for messages that are sent before the connection is established.
   */
  private sendBuffer: Array<() => void> = []

  /** The channels created by this socket. */
  private channels: Channel<any, any>[] = []

  /** Whether the socket is closed normally. */
  private closeWasClean = false

  /** The reconnect timeout calculation function. */
  private reconnectAfterMs: (tries: number) => number

  /** The heartbeat interval in millisecond. */
  private heartbeatIntervalMs: number

  /** The heartbeat timeout timer. */
  private heartbeatTimeoutTimer: ReturnType<typeof setTimeout> | undefined

  /** The heartbeat timer. */
  private heartbeatTimer: ReturnType<typeof setTimeout> | undefined

  /** The pending heartbeat push ref. */
  private pendingHeartbeatRef: string | undefined

  /** The socket URL params */
  private params: Record<string, string>

  /** The push timeout in millisecond. */
  private _pushTimeout: number

  /** The rejoin timeout calculation function. */
  private _rejoinAfterMs: (tries: number) => number

  /** The serializer version */
  private vsn: Version

  /**
   * @constructor
   *
   * @param endPoint The WebSocket endpoint. i.e
   *     - `ws://example.com/socket/websocket`
   *     - `wss://example.com/websocket`
   */
  constructor(
    private readonly endPoint: string,
    opts: Options = {},
  ) {
    this.heartbeatIntervalMs = opts.heartbeatIntervalMs ?? 30000
    this.reconnectAfterMs = (tries) => {
      if (opts.reconnectAfterMs) {
        return opts.reconnectAfterMs(tries)
      } else {
        return [1000, 10000, 20000][tries - 1] || 100000
      }
    }
    this._pushTimeout = opts.pushTimeout ?? DEFAULT_TIMEOUT

    this._rejoinAfterMs = (tries) => {
      if (opts.rejoinAfterMs) {
        return opts.rejoinAfterMs(tries)
      } else {
        return [1000, 10000, 20000][tries - 1] ?? 100000
      }
    }
    this.params = opts.params ?? {}
    this.reconnectTimer = new Timer(() => {
      this.teardown(() => this.connect())
    }, this.reconnectAfterMs)
    this.vsn = opts.vsn ?? '1.0.0'
  }

  /** @returns The timeout in ms to trigger push timeouts. */
  get pushTimeout() {
    return this._pushTimeout
  }

  /**
   * @returns The function that returns the ms rejoin interval for individual
   * channels.
   */
  get rejoinAfterMs() {
    return this._rejoinAfterMs
  }

  /**
   * @returns The socket protocol
   */
  protocol() {
    return location.protocol.match(/^https/) ? 'wss' : 'ws'
  }

  /**
   * @returns The fully qualified socket url
   */
  endPointURL() {
    const paramsStr = new URLSearchParams({
      ...this.params,
      vsn: this.vsn,
    }).toString()
    const uri = appendParams(this.endPoint, paramsStr)
    if (uri.charAt(0) !== '/') {
      return uri
    }
    if (uri.charAt(1) === '/') {
      return `${this.protocol()}:${uri}`
    }

    return `${this.protocol()}://${location.host}${uri}`
  }

  /**
   * Disconnects the socket
   *
   * See https://developer.mozilla.org/en-US/docs/Web/API/CloseEvent#Status_codes for valid status codes.
   *
   * @param callback Callback which is called after socket is disconnected.
   * @param code A status code for disconnection.
   * @param reason A textual description of the reason to disconnect.
   */
  disconnect(callback?: () => void, code?: number, reason?: string) {
    this.closeWasClean = true
    this.reconnectTimer.reset()
    this.teardown(callback, code, reason)
  }

  /** Connects the socket. */
  connect() {
    if (this.conn) {
      return
    }

    this.closeWasClean = false
    this.conn = new WebSocket(this.endPointURL())
    this.conn.binaryType = 'arraybuffer'
    this.conn.onopen = () => this.onConnOpen()
    this.conn.onerror = (error) => this.onConnError(error)
    this.conn.onmessage = (event) => this.onConnMessage(event)
    this.conn.onclose = (event) => this.onConnClose(event)
  }

  /** Reconnects the socket */
  reconnect() {
    this.reconnectTimer.reset()
    this.teardown(() => this.connect(), undefined, 'manual reconnect')
  }

  /**
   * Registers callbacks for connection open events
   *
   * @example socket.onOpen(() => { console.info("the socket was opened") })
   */
  onOpen(callback: () => void) {
    const ref = this.makeRef()
    this.stateChangeCallbacks.open.push([ref, callback])
    return ref
  }

  /**
   * Registers callbacks for connection close events
   */
  onClose(callback: (event: CloseEvent) => void) {
    const ref = this.makeRef()
    this.stateChangeCallbacks.close.push([ref, callback])
    return ref
  }

  /**
   * Registers callbacks for connection error events
   *
   * @example socket.onError(function(error){ alert("An error occurred") })
   */
  onError(callback: (event: Event) => void) {
    const ref = this.makeRef()
    this.stateChangeCallbacks.error.push([ref, callback])
    return ref
  }

  /**
   * Registers callbacks for connection message events
   */
  private onMessage(callback: (msg: any) => void) {
    const ref = this.makeRef()
    this.stateChangeCallbacks.message.push([ref, callback])
    return ref
  }

  /**
   * Pings the server and invokes the callback with the RTT in milliseconds
   *
   * @returns True if the ping was pushed or false if unable to be pushed.
   */
  ping(callback: (latency: number) => void) {
    if (!this.isConnected()) {
      return false
    }
    const ref = this.makeRef()
    const startTime = Date.now()
    this.push({ topic: 'phoenix', event: 'heartbeat', payload: {}, ref: ref })
    const onMsgRef = this.onMessage((msg) => {
      if (msg.ref === ref) {
        this.off([onMsgRef])
        callback(Date.now() - startTime)
      }
    })
    return true
  }

  /** Clears heartbeat timeouts. */
  private clearHeartbeats() {
    clearTimeout(this.heartbeatTimer)
    clearTimeout(this.heartbeatTimeoutTimer)
  }

  /** Callback called on connection open. */
  private onConnOpen() {
    logInfo('transport', `connected to ${this.endPointURL()}`)

    this.closeWasClean = false
    this.establishedConnections++
    this.flushSendBuffer()
    this.reconnectTimer.reset()
    this.resetHeartbeat()
    this.stateChangeCallbacks.open.forEach(([, callback]) => callback())
  }

  /** Triggers callback due to heartbeat timeout. */
  private heartbeatTimeout() {
    if (this.pendingHeartbeatRef) {
      this.pendingHeartbeatRef = undefined
      logInfo(
        'transport',
        'heartbeat timeout. Attempting to re-establish connection',
      )
      this.triggerChannelError()
      this.closeWasClean = false
      this.teardown(
        () => this.reconnectTimer.scheduleTimeout(),
        WS_CLOSE_NORMAL,
        'heartbeat timeout',
      )
    }
  }

  /** Resets heartbeat. */
  private resetHeartbeat() {
    this.pendingHeartbeatRef = undefined
    this.clearHeartbeats()
    this.heartbeatTimer = setTimeout(
      () => this.sendHeartbeat(),
      this.heartbeatIntervalMs,
    )
  }

  /**
   * Tears down the socket.
   *
   * @param callback Callback which is called after socket is disconnected.
   */
  private teardown(callback?: () => void, code?: number, reason?: string) {
    if (!this.conn) {
      return callback && callback()
    }

    this.waitForBufferDone(() => {
      if (this.conn) {
        if (code) {
          this.conn.close(code, reason || '')
        } else {
          this.conn.close()
        }
      }

      this.waitForSocketClosed(() => {
        if (this.conn) {
          this.conn.onopen = function () {} // noop
          this.conn.onerror = function () {} // noop
          this.conn.onmessage = function () {} // noop
          this.conn.onclose = function () {} // noop
          this.conn = undefined
        }

        callback && callback()
      })
    })
  }

  /**
   * Waits for the socket buffer to finish.
   *
   * @param callback Callback to be called after the buffer is finished.
   */
  private waitForBufferDone(callback: () => void, tries = 1) {
    if (tries === 5 || !this.conn || !this.conn.bufferedAmount) {
      callback()
      return
    }

    setTimeout(() => {
      this.waitForBufferDone(callback, tries + 1)
    }, 150 * tries)
  }

  /**
   * Waits for the socket to be closed.
   *
   * @param callback Callback to be called after socket is closed.
   */
  waitForSocketClosed(callback: () => void, tries = 1) {
    if (
      tries === 5 ||
      !this.conn ||
      this.conn.readyState === SOCKET_STATES.closed
    ) {
      callback()
      return
    }

    setTimeout(() => {
      this.waitForSocketClosed(callback, tries + 1)
    }, 150 * tries)
  }

  /** Callback called on connection closed. */
  private onConnClose(event) {
    const closeCode = event && event.code
    logInfo('transport', 'close', event)
    this.triggerChannelError()
    this.clearHeartbeats()
    if (!this.closeWasClean && closeCode !== 1000) {
      this.reconnectTimer.scheduleTimeout()
    }
    this.stateChangeCallbacks.close.forEach(([, callback]) => callback(event))
  }

  /** Callback called on connection error. */
  private onConnError(error: Event) {
    logInfo('transport', error)
    this.stateChangeCallbacks.error.forEach(([, callback]) => {
      callback(error)
    })
    if (this.establishedConnections > 0) {
      this.triggerChannelError()
    }
  }

  /** Triggers channel error callbacks for all open channels. */
  private triggerChannelError() {
    this.channels.forEach((channel) => {
      if (!(channel.isErrored() || channel.isLeaving() || channel.isClosed())) {
        channel.trigger('phx_error', {})
      }
    })
  }

  /**
   * @returns The connection state.
   */
  connectionState() {
    switch (this.conn && this.conn.readyState) {
      case SOCKET_STATES.connecting:
        return 'connecting'
      case SOCKET_STATES.open:
        return 'open'
      case SOCKET_STATES.closing:
        return 'closing'
      default:
        return 'closed'
    }
  }

  /**
   * @returns Whether the connection is open.
   */
  isConnected() {
    return this.connectionState() === 'open'
  }

  /** Removes the channel from the socket. */
  remove(channel: Channel<any, any>) {
    this.off(channel.stateChangeRefs)
    this.channels = this.channels.filter(
      (c) => c.joinRef() !== channel.joinRef(),
    )
  }

  /**
   * Removes `onOpen`, `onClose`, `onError,` and `onMessage` registrations.
   *
   * @param refs Lst of refs returned by calls to `onOpen`, `onClose`,
   *     `onError,` and `onMessage`
   */
  off(refs: string[]) {
    for (const key of Object.keys(this.stateChangeCallbacks)) {
      this.stateChangeCallbacks[key] = this.stateChangeCallbacks[key].filter(
        ([ref]) => {
          return refs.indexOf(ref) === -1
        },
      )
    }
  }

  /**
   * Initiates a new channel for the given topic.
   *
   * @param topic The channel topic
   * @param joinPayload Join request payload
   * @returns The new channel
   */
  channel<
    PushEventMap extends PhoenixPushEventMap = PhoenixPushEventMap,
    ServerEventMap extends PhoenixServerEventMap = PhoenixServerEventMap,
  >(topic: string, joinPayload: PushEventMap['phx_join']['request_payload']) {
    const chan = new Channel<PushEventMap, ServerEventMap>(
      topic,
      joinPayload,
      this,
    )
    this.channels.push(chan)
    return chan
  }

  /** Pushes a message to the socket. */
  push(data: PhoenixPushMessage) {
    const { topic, event, payload, ref } = data
    logInfo('push', `${topic} ${event} (${ref})`, payload)

    if (this.isConnected()) {
      encode(data, this.vsn, (result) => this.conn?.send(result))
    } else {
      this.sendBuffer.push(() =>
        encode(data, this.vsn, (result) => this.conn?.send(result)),
      )
    }
  }

  /**
   * @returns The next message ref, accounting for overflows
   */
  makeRef() {
    const newRef = this.ref + 1
    if (newRef === this.ref) {
      this.ref = 0
    } else {
      this.ref = newRef
    }

    return this.ref.toString()
  }

  /** Sends the heartbeat push. */
  private sendHeartbeat() {
    if (this.pendingHeartbeatRef && !this.isConnected()) {
      return
    }
    this.pendingHeartbeatRef = this.makeRef()
    this.push({
      topic: 'phoenix',
      event: 'heartbeat',
      payload: {},
      ref: this.pendingHeartbeatRef,
    })
    this.heartbeatTimeoutTimer = setTimeout(
      () => this.heartbeatTimeout(),
      this.heartbeatIntervalMs,
    )
  }

  /** Flushes the send buffer. */
  private flushSendBuffer() {
    if (this.isConnected() && this.sendBuffer.length > 0) {
      this.sendBuffer.forEach((callback) => callback())
      this.sendBuffer = []
    }
  }

  /** Socket message handler. */
  private onConnMessage(rawMessage: MessageEvent) {
    decode(rawMessage.data, this.vsn, (msg: PhoenixServerMessage) => {
      const { topic, event, payload, ref, join_ref } = msg
      if (ref && ref === this.pendingHeartbeatRef) {
        this.clearHeartbeats()
        this.pendingHeartbeatRef = undefined
        this.heartbeatTimer = setTimeout(
          () => this.sendHeartbeat(),
          this.heartbeatIntervalMs,
        )
      }

      logInfo(
        'receive',
        `${payload.status || ''} ${topic} ${event} ${
          (ref && '(' + ref + ')') || ''
        }`,
        payload,
      )

      for (let i = 0; i < this.channels.length; i++) {
        const channel = this.channels[i]
        if (!channel.isMember(topic, join_ref)) {
          continue
        }
        channel.trigger(event, payload, ref)
      }

      for (let i = 0; i < this.stateChangeCallbacks.message.length; i++) {
        const [, callback] = this.stateChangeCallbacks.message[i]
        callback(msg)
      }
    })
  }

  /** Leaves the open topic. */
  leaveOpenTopic(topic: string) {
    const dupChannel = this.channels.find(
      (c) => c.topic === topic && (c.isJoined() || c.isJoining()),
    )
    if (dupChannel) {
      logInfo('transport', `leaving duplicate topic "${topic}"`)
      dupChannel.leave()
    }
  }
}

/** Appends URL params string to a URL. */
function appendParams(url: string, paramsStr: string) {
  if (!paramsStr) {
    return url
  }

  const prefix = url.match(/\?/) ? '&' : '?'
  return `${url}${prefix}${paramsStr}`
}
